import { BasicWindow } from "./BasicWindow";
import { Global } from "../../utils/Global";
import { BaseItem, ItemType, Rarity } from "../../inventory/BaseItem";
import { Effect } from "../../inventory/Effects";

export class ItemDescriptor extends BasicWindow {
  private titleText!: Phaser.GameObjects.BitmapText;
  private typeText!: Phaser.GameObjects.BitmapText;
  private rarityText!: Phaser.GameObjects.BitmapText;
  private valueText!: Phaser.GameObjects.BitmapText;
  private effectsText!: Phaser.GameObjects.BitmapText;
  private itemIcon!: Phaser.GameObjects.Image;

  // Dynamic effects display elements
  private effectElements: Phaser.GameObjects.GameObject[] = [];

  private currentItem?: BaseItem;
  private showDelay: number = 300; // ms delay before showing
  private showTimer?: Phaser.Time.TimerEvent;

  constructor(scene: Phaser.Scene) {
    super(
      scene,
      0, // x
      0, // y
      200, // width
      150, // height
      "Button1", // textureKey
      "", // frameKey
      200, // sliceWidth
      150, // sliceHeight
      7, // leftWidth
      7, // topHeight
      7, // rightWidth
      7, // bottomHeight
    );

    this.createContent();
    this.setDepth(25000); // Above items and level report, below pause menu
  }

  private createContent(): void {
    const contentContainer = this.getContentContainer();

    // Create text elements with temporary positions (will be repositioned dynamically)
    // Title (item name derived from key)
    this.titleText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Item Name",
      Global.minFontSize * 1.2,
    ).setOrigin(0, 0.5);
    contentContainer.add(this.titleText);

    // Item icon (will be updated dynamically when showing item)
    this.itemIcon = new Phaser.GameObjects.Image(
      this.scene,
      0,
      0,
      "items",
      "item_screw.png", // Default frame that exists
    ).setOrigin(0, 0.5);
    contentContainer.add(this.itemIcon);

    // Type
    this.typeText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Type: Consumable",
      Global.minFontSize,
    ).setOrigin(0, 0.5);
    contentContainer.add(this.typeText);

    // Rarity
    this.rarityText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Rarity: Common",
      Global.minFontSize,
    ).setOrigin(0, 0.5);
    contentContainer.add(this.rarityText);

    // Value/Price
    this.valueText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Value: $0",
      Global.minFontSize,
    ).setOrigin(0, 0.5);
    contentContainer.add(this.valueText);

    // Effects
    this.effectsText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Effects: None",
      Global.minFontSize,
    ).setOrigin(0, 0.5);
    contentContainer.add(this.effectsText);
  }

  public showForItem(item: BaseItem, mouseX: number, mouseY: number): void {
    // Clear any existing timers
    this.clearTimers();

    this.currentItem = item;

    // Set up show timer
    this.showTimer = this.scene.time.delayedCall(this.showDelay, () => {
      if (this.currentItem === item) {
        // Update content but don't show yet
        this.updateContentAndResize(item, () => {
          // Only show after content is properly sized and positioned
          this.positionNearMouse(mouseX, mouseY);
          this.show();
        });
      }
    });
  }

  public hideDescriptor(): void {
    // Clear any existing timers
    this.clearTimers();

    this.hide();
    this.currentItem = undefined;
  }

  private clearTimers(): void {
    if (this.showTimer) {
      this.showTimer.destroy();
      this.showTimer = undefined;
    }
  }

  private updateContentAndResize(
    item: BaseItem,
    onComplete?: () => void,
  ): void {
    const lineSpacing = Global.minFontSize * 0.3;

    // Clear any existing effect elements first
    this.clearEffectElements();

    // Reset all text elements to clean positions (0,0) before positioning
    this.titleText.setPosition(0, 0);
    this.typeText.setPosition(0, 0);
    this.rarityText.setPosition(0, 0);
    this.valueText.setPosition(0, 0);
    this.effectsText.setPosition(0, 0);
    this.itemIcon.setPosition(0, 0);

    // Position content starting from top, let resizeToFitContent handle centering
    const fontSize = Global.minFontSize;
    const titleHeight = fontSize * 1.2;
    const iconSize = fontSize * 2; // Icon size

    let currentY = 0; // Start from top, will be centered later

    // Title (item name from key)
    const itemName = this.getItemName(item);
    this.titleText.setText(itemName);
    this.titleText.setFontSize(fontSize * 1.2);
    this.titleText.setOrigin(0, 0.5);
    this.titleText.setPosition(0, currentY);
    currentY += titleHeight + lineSpacing;

    // Icon positioned next to type
    this.itemIcon.setTexture(item.textureKey, item.frameKey);
    this.itemIcon.setDisplaySize(iconSize, iconSize);
    this.itemIcon.setOrigin(0, 0.5);
    this.itemIcon.setPosition(0, currentY);

    // Type
    const typeName = this.getTypeName(item.itemType);
    this.typeText.setText(`Type: ${typeName}`);
    this.typeText.setFontSize(fontSize);
    this.typeText.setOrigin(0, 0.5);
    this.typeText.setPosition(iconSize + fontSize * 0.5, currentY);
    currentY += Math.max(iconSize, fontSize) + lineSpacing;

    // Rarity
    const rarityName = this.getRarityName(item.rarity);
    this.rarityText.setText(`Rarity: ${rarityName}`);
    this.rarityText.setFontSize(fontSize);
    this.rarityText.setOrigin(0, 0.5);
    this.rarityText.setPosition(0, currentY);
    // Apply rarity color
    this.rarityText.setTint(this.getRarityColor(item.rarity));
    currentY += fontSize + lineSpacing;

    // Value/Price
    const value = item.value || 0;
    this.valueText.setText(`Value: $${value}`);
    this.valueText.setFontSize(fontSize);
    this.valueText.setOrigin(0, 0.5);
    this.valueText.setPosition(0, currentY);
    this.valueText.setTint(0xfff57a);
    currentY += fontSize + lineSpacing;

    // Effects
    if (!item.effects || item.effects.length === 0) {
      this.effectsText.setText("Effects: None");
      this.effectsText.setFontSize(fontSize);
      this.effectsText.setOrigin(0, 0.5);
      this.effectsText.setPosition(0, currentY);
    } else {
      this.effectsText.setText("Effects:");
      this.effectsText.setFontSize(fontSize);
      this.effectsText.setOrigin(0, 0.5);
      this.effectsText.setPosition(0, currentY);
      currentY += fontSize + lineSpacing;

      // Create bullet points for each effect
      item.effects.forEach((effect) => {
        currentY = this.createEffectBulletPoint(
          effect,
          currentY,
          lineSpacing,
          0,
        );
      });
    }

    // Defer resize to next frame to ensure all text bounds are properly calculated
    this.scene.time.delayedCall(1, () => {
      this.resizeToFitContent();
      // Call the completion callback after resizing is done
      if (onComplete) {
        onComplete();
      }
    });
  }

  private clearEffectElements(): void {
    // Remove all dynamic effect elements from content container
    this.effectElements.forEach((element) => {
      this.getContentContainer().remove(element);
      element.destroy();
    });
    this.effectElements = [];
  }

  private createEffectBulletPoint(
    effect: Effect,
    startY: number,
    lineSpacing: number,
    baseX: number,
  ): number {
    const iconSize = Global.minFontSize * 1.2;
    const Indent = Global.minFontSize;
    let currentY = startY;

    // Create effect icon
    const icon = new Phaser.GameObjects.Image(
      this.scene,
      baseX + Indent + Global.minFontSize,
      currentY,
      "items",
      effect.icon,
    ).setOrigin(0, 0.5);
    icon.setDisplaySize(iconSize, iconSize);
    this.getContentContainer().add(icon);
    this.effectElements.push(icon);

    // Create effect name
    const effectName = effect.name || "Effect";
    const nameText = new Phaser.GameObjects.BitmapText(
      this.scene,
      baseX + Indent + Global.minFontSize + iconSize + Global.minFontSize * 0.2,
      currentY,
      "fnt_paintBasic",
      effectName,
      Global.minFontSize,
    ).setOrigin(0, 0.5);
    this.getContentContainer().add(nameText);
    this.effectElements.push(nameText);

    currentY += Global.minFontSize + lineSpacing * 0.5;

    // Create duration text
    if (effect.duration != -1) {
      const durationText =
        effect.duration === 0
          ? "Single Use"
          : `Triggers left: ${effect.duration.toString()}`;
      const durationDisplay = new Phaser.GameObjects.BitmapText(
        this.scene,
        baseX + Indent + Global.minFontSize,
        currentY,
        "fnt_paintBasic",
        durationText,
        Global.minFontSize,
      ).setOrigin(0, 0.5);
      durationDisplay.setTint(0xaaaaaa);
      this.getContentContainer().add(durationDisplay);
      this.effectElements.push(durationDisplay);
      currentY += Global.minFontSize + lineSpacing * 0.3;
    }

    // Create effect description
    const descText = new Phaser.GameObjects.BitmapText(
      this.scene,
      baseX + Indent + Global.minFontSize,
      currentY,
      "fnt_paintBasic",
      effect.description,
      Global.minFontSize,
    ).setOrigin(0, 0.5);
    descText.setTint(0xcccccc);
    this.getContentContainer().add(descText);
    this.effectElements.push(descText);

    currentY += Global.minFontSize + lineSpacing;

    return currentY;
  }

  private getItemName(item: BaseItem): string {
    // Convert key to readable name (e.g., "SodaBottle" -> "Soda Bottle")
    const key = (item as unknown as { key: string }).key || "Unknown Item";
    return key.replace(/([A-Z])/g, " $1").trim();
  }

  private getTypeName(itemType: ItemType): string {
    switch (itemType) {
      case ItemType.CONSUMABLE:
        return "Consumable";
      case ItemType.MODIFIER:
        return "Modifier";
      case ItemType.SECTION:
        return "Section";
      case ItemType.CHARM:
        return "Charm";
      default:
        return "Unknown";
    }
  }

  private getRarityName(rarity: Rarity): string {
    switch (rarity) {
      case Rarity.COMMON:
        return "Common";
      case Rarity.UNCOMMON:
        return "Uncommon";
      case Rarity.RARE:
        return "Rare";
      case Rarity.EPIC:
        return "Epic";
      case Rarity.LEGENDARY:
        return "Legendary";
      default:
        return "Unknown";
    }
  }

  private getRarityColor(rarity: Rarity): number {
    switch (rarity) {
      case Rarity.COMMON:
        return 0xffffff;
      case Rarity.UNCOMMON:
        return 0x8de35d;
      case Rarity.RARE:
        return 0x4837c7;
      case Rarity.EPIC:
        return 0x9e2cab;
      case Rarity.LEGENDARY:
        return 0xfa6735;
      default:
        return 0xffffff;
    }
  }

  private positionNearMouse(mouseX: number, mouseY: number): void {
    const padding = Global.minFontSize;
    const windowBounds = this.getBounds();
    const windowWidth = windowBounds.width;
    const windowHeight = windowBounds.height;
    const screenWidth = this.scene.scale.width;
    const screenHeight = this.scene.scale.height;

    // Default position: to the right and slightly below the mouse
    let x = mouseX + windowWidth / 2 + padding;
    let y = mouseY;

    // Check horizontal positioning
    if (x + windowWidth / 2 > screenWidth) {
      // Window would go off the right edge, position to the left of mouse
      x = mouseX - windowWidth / 2 - padding;
    }

    // Check vertical positioning
    if (y + windowHeight / 2 > screenHeight) {
      // Window would go off the bottom edge, position above mouse
      y = mouseY - windowHeight / 2 - padding;
    } else if (y - windowHeight / 2 < 0) {
      // Window would go off the top edge, position below mouse
      y = mouseY + windowHeight / 2 + padding;
    }

    // Final boundary checks to ensure window stays fully on screen
    x = Math.max(windowWidth / 2, Math.min(screenWidth - windowWidth / 2, x));
    y = Math.max(
      windowHeight / 2,
      Math.min(screenHeight - windowHeight / 2, y),
    );

    this.setPosition(x, y);
  }

  public override destroy(): void {
    this.clearTimers();
    this.clearEffectElements();
    if (this.scene) this.scene.tweens.killTweensOf(this);
    super.destroy();
  }
}
